import React, { useState } from 'react';
import { notification } from 'antd';
import type { TablePaginationConfig, TableProps } from 'antd';
import {
  ICollector,
  ISearchCollectorReq,
  ISearchCollectorRes,
} from '@/interfaces/collector';
import { EyeFilled, DeleteFilled, FileSearchOutlined } from '@ant-design/icons';
// import { deleteCollector, recoverCollector } from '@/adapters/collector';
import { IDataModal } from '@/interfaces/dataModal';
import DeleteConfirmModal from '@/components/modal/confirm-delete';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import TableComponent from '@/components/table';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import { twMerge } from 'tailwind-merge';
import { isManager } from '@/utils/permissionUtils';
import { deleteCollector } from '@/adapters/collector';
import { ECollectorType } from '@/enums/ECollector';
import { IMedia } from '@/interfaces/media';
import { mediaDownload } from '@/utils/mediaUtils';

interface IProps {
  loading: boolean;
  criteria: ISearchCollectorReq;
  collector: ISearchCollectorRes;
  onDelete: (id: number) => void;
  onEditData: (item: ICollector) => void;
  onTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ICollector> | SorterResult<ICollector>[],
  ) => void;
}

const _collectorModal: IDataModal<ICollector> = {
  open: false,
};

const CollectorTable = ({
  loading,
  criteria,
  collector,
  onDelete,
  onEditData,
  onTableChange,
}: IProps) => {
  const perManager = isManager();
  const [deleteModal, setDeleteModal] =
    useState<IDataModal<ICollector>>(_collectorModal);
  const onDeleteCollector = async () => {
    try {
      await deleteCollector(Number(deleteModal?.id));
      notification.success({
        message: 'ลบ Collector สำเร็จ',
      });
      onDelete(Number(deleteModal?.id || 0));
      setDeleteModal({ ..._collectorModal });
    } catch (error) {
      console.log(error);
    }
  };

  const handleDownload = async (mediaId: number, name: string) => {
    const media: IMedia = {
      id: mediaId,
      name: name,
    } as IMedia;
    await mediaDownload(media);
  };

  const columns: TableProps<ICollector>['columns'] = [
    {
      title: 'วันที่',
      dataIndex: 'collectorDate',
      key: 'collectorDate',
      className: 'font-bold',
      width: 130,
      fixed: 'left',
      align: 'center',
      render: _ => formatDate(_),
    },
    {
      title: 'Collector Type',
      dataIndex: 'collectorType',
      key: 'collectorType',
      className: 'font-bold',
      align: 'center',
      width: 130,
      render: _ => (
        <div>
          {_ === ECollectorType.BILLING
            ? 'วางบิล'
            : _ === ECollectorType.PAYMENT_RECEIVED
              ? 'รับเช็ค'
              : 'เข้าเช็ค'}
        </div>
      ),
    },
    {
      title: 'Collector Name',
      dataIndex: 'createByName',
      key: 'createByName',
      className: 'font-bold',
      width: 200,
    },
    {
      title: 'รายการ',
      dataIndex: 'collectorItems',
      key: 'collectorItems',
      className: 'font-bold',
      width: 130,
      align: 'center',
      render: (_, rec) => formatNumber(rec?.collectorItems?.length || 0),
    },
    {
      title: 'หมายเหตุ',
      dataIndex: 'remark',
      key: 'remark',
      className: 'font-bold',
    },
    {
      title: 'FILE',
      dataIndex: 'mediaId',
      key: 'mediaId',
      width: 50,
      align: 'center',
      render: (_, rec) => (
        <FileSearchOutlined
          className="text-blue-700 px-1.5"
          onClick={() => handleDownload(_, rec.remark)}
        />
      ),
    },
    {
      title: 'สถานะ',
      dataIndex: 'isConfirm',
      key: 'isConfirm',
      className: 'font-bold',
      align: 'center',
      width: 120,
      render: _ => (
        <div
          className={twMerge(
            'rounded py-0.5 bg-yellow-100',
            _ && ' bg-green-100',
          )}
        >
          {_ ? 'Confirm' : 'Draft'}
        </div>
      ),
    },
    {
      dataIndex: 'action',
      key: 'action',
      width: 80,
      render: (_, rec) => (
        <div className="flex flex-row gap-4 items-center w-full justify-end">
          <div className="flex flex-row divide-x divide-dashed items-center w-full justify-end">
            {(!rec.isConfirm || perManager) && (
              <DeleteFilled
                className="text-red-700 px-1.5"
                onClick={() =>
                  setDeleteModal({
                    open: true,
                    item: rec,
                    id: rec.id,
                  })
                }
              />
            )}

            <EyeFilled
              className="text-blue-500 px-1.5"
              onClick={() => onEditData(rec)}
            />
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<ICollector>
        columns={columns}
        criteria={criteria}
        data={collector}
        loading={loading}
        onTableChange={onTableChange}
        scrollLength={1200}
      />
      <DeleteConfirmModal
        content={`คุณต้องการลบใช่หรือไม่`}
        handleCancel={() => {
          setDeleteModal({ ..._collectorModal });
        }}
        handleOk={() => {
          onDeleteCollector();
        }}
        open={deleteModal?.open}
        title={`Collector '${deleteModal?.item?.collectorDate}'`}
      />
    </div>
  );
};

export default CollectorTable;
